import { IonButton, IonButtons, IonCard, IonCardContent, IonContent, IonFooter, IonHeader, IonIcon, IonItem, IonLabel, IonList, IonPage, IonText, IonTitle, IonToolbar, useIonAlert } from "@ionic/react";
import React, { useRef, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../../../services/redux/hook";
import { useHistory } from "react-router";
import { checkmarkCircleOutline, closeOutline, documentLockOutline } from "ionicons/icons";
import { closeModal } from "../../../../services/redux/slices/opened_navigation_modal/opened_root_modal";
import { useModuleTranslations } from "../../../../hooks/useModuleTranslations";
import { ebioroApi, useCreateVirtualAccountsKycLinkMutation, useGetUserInfoQuery, useRefreshVirtualAccountsKycLinkQuery } from "../../../../services/api/ebioro/ebioro";
import { Browser } from "@capacitor/browser";

const VirtualAccountCreateKYC: React.FC = () => {
  const pageRef = useRef<HTMLElement | null>(null);
  const { t } = useTranslation();
  const { mt: kt } = useModuleTranslations("kyc");
  const dispatch = useAppDispatch();
  const history = useHistory();
  const [presentAlert] = useIonAlert();
  const { data: userInfo } = useGetUserInfoQuery(undefined);
  const [createKycLink, { isLoading }] = useCreateVirtualAccountsKycLinkMutation();
  const [submitting, setSubmitting] = useState(false);

//   const redirectUri = useMemo(() => `ebioro://kyc/callback`, []);

  const close = () => dispatch(closeModal());

  const onConfirm = async () => {
    if (!userInfo?.email) {
      presentAlert({ header: "Error", message: "Missing user email.", buttons: ["OK"] });
      return;
    }
    try {   
      setSubmitting(true);
      const res = await createKycLink({ email: userInfo.email, type: "individual", /*redirect_uri: redirectUri*/ }).unwrap();
      const kycLinkId = res.id;

      const refreshResponse = await dispatch(ebioroApi.endpoints.refreshVirtualAccountsKycLink.initiate({ linkId: kycLinkId })).unwrap();

      const tosUrl: string | undefined = refreshResponse?.tos_link;
      const tosStatus = refreshResponse?.tos_status;
      const kycUrl: string | undefined = refreshResponse?.kyc_link;

      if (!tosUrl || !kycUrl) throw new Error("Missing TOS/KYC URLs.");

      if (tosStatus === 'approved') {
        // If TOS is approved, open KYC directly
        await Browser.open({ url: kycUrl });
        return;
      }

      // Open TOS first, then when the browser is closed, open KYC
      const finishedListener = await Browser.addListener("browserFinished", async () => {
        try {
          await Browser.open({ url: kycUrl });
        } finally {
          finishedListener.remove();
        }
      });

      await Browser.open({ url: tosUrl });
    } catch (e: any) {
      presentAlert({ header: "Error", message: e?.data?.message || e?.message || "Unable to start verification.", buttons: ["OK"] });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <IonPage ref={pageRef as any}>
      <IonHeader className="ion-no-border">
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton onClick={close} color="primary">
              <IonIcon slot="start" icon={closeOutline} />
            </IonButton>
          </IonButtons>
          <IonTitle>{kt("page.title")}</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent className="ion-padding">
        <IonCard style={{ borderRadius: 16, marginTop: "10%" }}>
          <IonCardContent>
            <IonItem lines="none">
              <IonIcon slot="start" color="primary" icon={documentLockOutline} />
              <h1 style={{ margin: 0, fontWeight: 700 }}>{kt("info.noBankAccount")}</h1>
            </IonItem>
            <IonText color="primary" className="ion-text-center" style={{ display: "block", marginTop: "5%" }}>
              <p style={{ margin: 0 }}>{kt("page.subtitle")}</p>
            </IonText>
          </IonCardContent>
        </IonCard>

        <div style={{ marginTop: 18 }}>
          <h3 style={{ margin: "0 0 8px 0", fontWeight: 700 }}>{kt("info.whyVerify")}</h3>
          <IonText className="ion-text-wrap ion-text-center"><p style={{ margin: "5%" }}>{kt("info.whyVerifyDesc")}</p></IonText>
        </div>

        <div style={{ marginTop: "10%" }}>
          <h3 style={{ margin: "10%", fontWeight: 700 }}>{kt("info.whatHappensNext") ?? "What happens next?"}</h3>
          <IonList lines="none" className="bg-transparent">
            <IonItem><IonIcon slot="start" color="success" icon={checkmarkCircleOutline} /><IonLabel>{kt("steps.identity") ?? "Complete identity verification"}</IonLabel></IonItem>
            <IonItem><IonIcon slot="start" color="success" icon={checkmarkCircleOutline} /><IonLabel>{kt("steps.details") ?? "Get your personal USD or EUR bank account details"}</IonLabel></IonItem>
            <IonItem><IonIcon slot="start" color="success" icon={checkmarkCircleOutline} /><IonLabel>{kt("steps.receive") ?? "Receive payments directly into your Ebioro account"}</IonLabel></IonItem>
          </IonList>
        </div>
      </IonContent>

      <IonFooter className="ion-no-border">
        <IonToolbar>
          <IonButton 
            expand="block" 
            color="primary" 
            shape="round" 
            id='pageFooterButton'
            onClick={onConfirm} disabled={submitting || isLoading}>
            {submitting || isLoading ? t("loading") ?? "Loading..." : kt("buttons.confirm")}
          </IonButton>
        </IonToolbar>
      </IonFooter>
    </IonPage>
  );
};

export default VirtualAccountCreateKYC;